import { FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Box, Paper, Stack, styled, Table, TableBody, Typography } from '@mui/material';

import { Loading, Tabs } from '@components/base';
import useEpisodesQuery from '@hooks/queryHooks/useEpisodesQuery';
import { useProviderNames } from '@hooks/useProviderNames';
import useTitleContext from '@hooks/useTitleContext';
import { DetailedTitle, Season } from '@models/Title';

import EpisodeTableRow from './EpisodeTableRow';

const StyledTable = styled(Table)`
  width: 100%;

  & td {
    cursor: pointer;
  }
`;

const Episodes: FC = () => {
  const { t } = useTranslation();
  const titleContext = useTitleContext();
  const { providerIdentifier } = titleContext;

  const { data: episodes, isLoading, isError } = useEpisodesQuery(titleContext);
  const { getProviderName } = useProviderNames();

  const filteredEpisodes = useMemo(() => {
    if (providerIdentifier)
      return episodes?.reduce((acc: DetailedTitle[], episode) => {
        if (
          episode.playbackOptions.some(
            playbackOption => playbackOption.providerIdentifier === providerIdentifier
          )
        )
          return [
            ...acc,
            {
              ...episode,
              playbackOptions: episode.playbackOptions.filter(
                playbackOption => playbackOption.providerIdentifier === providerIdentifier
              ),
            },
          ];

        return acc;
      }, []);

    return episodes;
  }, [episodes, providerIdentifier]);

  const seasonNumbers = filteredEpisodes
    ?.map(e => e.season?.sequenceNumber)
    .filter((season, index, seasons) => seasons.indexOf(season) === index);

  const seasons: Season[] | undefined = seasonNumbers
    ?.reduce((acc: Season[], seasonNumber) => {
      const filtered = filteredEpisodes?.find(
        e => e.season?.sequenceNumber === seasonNumber
      )?.season;

      return filtered ? [...acc, filtered] : acc;
    }, [])
    .sort((a, b) => (a?.sequenceNumber || 0) - (b?.sequenceNumber || 0));

  const [selectedSeason, setSelectedSeason] = useState(seasons?.[0]?.sequenceNumber);

  useEffect(() => {
    if (isLoading) return;
    if (seasons)
      if (
        !selectedSeason ||
        !seasons.some(({ sequenceNumber }) => sequenceNumber === selectedSeason)
      )
        setSelectedSeason(seasons[0]?.sequenceNumber);
  }, [seasons, isLoading, selectedSeason]);

  return isLoading ? (
    <Loading />
  ) : isError ? (
    <Box p={2}>
      <Alert severity='error'>{t('errorMessage', { context: 'episodes' })}</Alert>
    </Box>
  ) : episodes?.length === 0 ? (
    <Box p={2}>
      <Alert severity='info'>{t('episodes.empty')}</Alert>
    </Box>
  ) : providerIdentifier && filteredEpisodes?.length === 0 ? (
    <Box p={2}>
      <Alert severity='info'>
        {t('episodes.empty', { context: 'source', source: getProviderName(providerIdentifier) })}
      </Alert>
    </Box>
  ) : (
    <Tabs
      buttonVariant='toggleButton'
      onTabChange={newValue => setSelectedSeason(Number(newValue))}
      selectedTab={selectedSeason?.toString() ?? ''}
      tabs={
        seasons?.map(season => ({
          value: season.sequenceNumber.toString(),
          title: <Box minWidth={18}>{season.sequenceNumber}</Box>,
          BoxProps: { pt: '0 !important' },
          content: (
            <Stack spacing={2}>
              <Box>
                <Typography color='textSecondary' variant='caption'>
                  {t('season.count', { count: season.sequenceNumber })}
                </Typography>
                <Typography variant='h4'>{season?.titleName}</Typography>
              </Box>

              <Paper>
                <StyledTable>
                  <TableBody>
                    {filteredEpisodes
                      ?.filter(e => e.season?.sequenceNumber === season?.sequenceNumber)
                      .sort(
                        (a, b) =>
                          (a.episode?.sequenceNumber || 0) - (b.episode?.sequenceNumber || 0)
                      )
                      .map(episode => (
                        <EpisodeTableRow episode={episode} key={episode.masterId} />
                      ))}
                  </TableBody>
                </StyledTable>
              </Paper>
            </Stack>
          ),
        })) ?? []
      }
    />
  );
};

export default Episodes;
